// @ts-nocheck
import { useCallback, useEffect, useState } from 'react';
import { Alert, Snackbar, Stack, Typography } from 'components/base';
import { makeStyles } from '@material-ui/core';
import { EventBusyIcon } from 'assets/icons';
import { text } from 'assets/colors/colors';
import ConfirmationModal from './__v2__/ConfirmationModal';
import { useGet, useUser } from '../hooks';
import WarningModal from './__v5__/Modals/WarningModal/WarningModal';
import {
  formatDate,
  parseDate,
  startOfTime,
  TimeUnit,
  differenceInTime,
} from 'utils/dateUtils';

const useStyle = makeStyles({
  alertTitle: {
    fontSize: '26px !important',
    color: '#e13939',
  },
  iconStyle: {
    color: '#E13939',
    fontSize: '65px',
    marginBottom: '150px',
  },
  descriptionTitle: {
    fontSize: '18px !important',
    color: '#E13939',
  },
  descriptionSubtitle: {
    '&.MuiTypography-root': {
      marginTop: '16px',
      marginBottom: '16px',
    },
    color: '#000000',
  },
  dialogStyle: {
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
    },
  },
  emailLink: {
    color: '#FBAD33',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      color: '#FBAD33',
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  },
  subtitle2: {
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
    letterSpacing: '0.1px',
    textAlign: 'left',
    color: '#F0840F',
  },
  body: {
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20.02px',
    letterSpacing: '0.15px',
    textAlign: 'left',
    color: text.primary,
  },
  caption: {
    fontFamily: 'Roboto',
    fontSize: '12px',
    fontWeight: 700,
    lineHeight: '18px',
    letterSpacing: '0.15px',
    textAlign: 'left',
  },
  secondaryText: {
    color: text.secondary,
  },
  boldFontWeight: {
    fontWeight: 'bold',
  },
});
const SubscriptionExpire = () => {
  const classes = useStyle();
  const { user, logout } = useUser();
  const token = localStorage.getItem('token');
  const [isMaintenanceModal, setIsMaintenanceModal] = useState(false);
  const [tenantId, setTenantId] = useState(localStorage.getItem('tenant'));
  const [openSubscriptionExpiredModal, setOpenSubscriptionExpiredModal] =
    useState(false);
  const [
    openSubscriptionGracePeriodModal,
    setOpenSubscriptionGracePeriodModal,
  ] = useState(false);
  const [
    openSubscriptionModalIsExpiring,
    setIsOpenSubscriptionModalIsExpiring,
  ] = useState(false);

  const [remainingDays, setRemainingDays] = useState(0);
  const userHasCanManageOrgPermission =
    user?.data?.organisation?.can_manage_organisation;

  const handleSubscriptionGracePeriodModalClose = useCallback(() => {
    setOpenSubscriptionGracePeriodModal(false);
  }, []);

  const handleSubscriptionStatus = useCallback((data) => {
    if (data) {
      setIsMaintenanceModal(data?.is_maintenance);
      if (data?.is_server_terminated && data?.expired_date) {
        const expiredDate = startOfTime(
          parseDate(data?.expired_date),
          TimeUnit.DAYS,
        );
        const currentDate = startOfTime(new Date(), TimeUnit.DAYS);
        const diffDays = differenceInTime(
          currentDate,
          expiredDate,
          TimeUnit.DAYS,
        );
        setRemainingDays(diffDays);

        // Grace period: from expired date to 30 days after
        const isInGracePeriod = diffDays >= 0 && diffDays <= 30;

        // Going to expire: within 15 days before the expired date
        const isGoingToExpire = diffDays > -15 && diffDays < 0;

        // Grace period exceeded: more than 30 days after expired date
        const gracePeriodExceeded = diffDays > 30;

        if (gracePeriodExceeded) {
          setOpenSubscriptionExpiredModal(true);
        }
        if (isInGracePeriod) {
          setOpenSubscriptionGracePeriodModal(true);
        }
        if (isGoingToExpire) {
          setIsOpenSubscriptionModalIsExpiring(true);
        }
      }
    }
  }, []);

  const { data, fetch: fetchSubscriptionStatus } = useGet(
    'subscription_status',
    {
      skipToken: true,
      customSkipFn: ({ query }) => !query.tenant_id && !user,
      query: {
        version: '0.2',
      },
      onSuccess: handleSubscriptionStatus,
    },
  );

  useEffect(() => {
    const checkTenant = () => {
      const currentTenantId = localStorage.getItem('tenant');
      if (currentTenantId !== tenantId) {
        setTenantId(currentTenantId);
      }
    };
    checkTenant();
    const intervalId = setInterval(checkTenant, 1000);
    return () => clearInterval(intervalId);
  }, [tenantId]);

  useEffect(() => {
    if (tenantId && token) {
      fetchSubscriptionStatus({
        query: { tenant_id: tenantId, version: '0.2' },
      });
    }
  }, [tenantId]);

  const formattedExpiredDate = formatDate(
    new Date(data?.expired_date ?? new Date()),
    'dd/MM/yyyy',
  );
  const daysRemaining = 30 - Number(remainingDays);
  const gracePeriodDaysRemaining =
    daysRemaining === 1 ? `${daysRemaining} day` : `${daysRemaining} days`;

  return (
    <>
      <ConfirmationModal
        openModal={isMaintenanceModal}
        title={
          <Typography variant="subtitle2" className={classes.alertTitle}>
            Alert!
          </Typography>
        }
        className={classes.dialogStyle}
        firstButton={null}
        deleteButtonLabel="Log out"
        content={
          <>
            <Typography
              variant="subtitle2"
              className={classes.descriptionTitle}
            >
              Maintainance Break
            </Typography>
            <Typography className={classes.descriptionSubtitle}>
              This is a friendly reminder that we have scheduled maintenance for
              Varicon today . During this maintenance window, our systems will
              be temporarily unavailable. We apologize for any inconvenience
              this may cause and appreciate your understanding.
            </Typography>
          </>
        }
        isFirstButtonHidden
        isSecondButtonHidden
        isCloseHidden
        isDivider
        maxWidth="sm"
        handleDelete={() => {}}
      />
      <ConfirmationModal
        openModal={openSubscriptionExpiredModal}
        title={
          <Typography variant="subtitle2" className={classes.alertTitle}>
            Alert!
          </Typography>
        }
        className={classes.dialogStyle}
        firstButton={null}
        deleteButtonLabel="Log out"
        content={
          <>
            <Typography
              variant="subtitle2"
              className={classes.descriptionTitle}
            >
              Subscription Expired
            </Typography>
            <Typography className={classes.descriptionSubtitle}>
              {data?.organization_title}&apos;s Varicon subscription has expired
              on {formattedExpiredDate}, and the 30-day grace period has also
              ended. Access to Varicon is no longer possible.
              <br />
              <br />
              Please contact your company administrator or Varicon Support to
              renew your subscription and restore access.
            </Typography>
            <Typography>
              For support, reach out to us at{' '}
              <a
                href="mailto:support@varicon.com.au"
                className={classes.emailLink}
                target="_blank"
                rel="noreferrer"
              >
                support@varicon.com.au
              </a>
            </Typography>
          </>
        }
        icon={<EventBusyIcon className={classes.iconStyle} />}
        isFirstButtonHidden
        isCloseHidden
        isDivider
        maxWidth="sm"
        handleDelete={() => {
          logout();
        }}
        enableEmailLink
      />
      {userHasCanManageOrgPermission && (
        <WarningModal
          isOpen={openSubscriptionGracePeriodModal}
          handleClose={() => {
            handleSubscriptionGracePeriodModalClose();
          }}
          dialogTitle="Information"
          dialogContent={
            <Stack spacing={2}>
              <Typography className={classes.subtitle2}>
                Subscription Grace Period!
              </Typography>
              <Typography className={classes.body}>
                {data?.organization_title}&apos;s Varicon subscription has
                expired on {formattedExpiredDate}. As a courtesy, we have
                extended your access for an additional 30 days. After this grace
                period ends, you will no longer be able to use Varicon.
              </Typography>
              <Typography className={classes.body}>
                <span
                  className={[classes.body, classes.boldFontWeight].join(' ')}
                >
                  {gracePeriodDaysRemaining}
                </span>{' '}
                remaining on your Grace Period. Renew now to maintain
                uninterrupted access
              </Typography>
              <Typography
                className={`${classes.body} ${classes.secondaryText}`}
              >
                For more information about your subscription status, please
                reach out to your company administrator or contact Varicon
                Support at{' '}
                <a
                  href="mailto:support@varicon.com.au"
                  className={classes.emailLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  support@varicon.com.au
                </a>
              </Typography>
            </Stack>
          }
          showDialogActions={false}
        />
      )}

      {userHasCanManageOrgPermission && (
        <Snackbar
          open={openSubscriptionModalIsExpiring}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ width: '100%', paddingTop: '1rem' }}
        >
          <Alert
            severity="warning"
            onClose={() => {
              setIsOpenSubscriptionModalIsExpiring(false);
            }}
          >
            <span className={[classes.body, classes.boldFontWeight].join(' ')}>
              {`${Math.abs(remainingDays)} ${
                Math.abs(remainingDays) === 1 ? 'day' : 'days'
              } `}
            </span>
            <span className={classes.body}>
              remaining on your subscription. Please contact Varicon Support to
              extend your subscription.
            </span>
            <a
              href="mailto:support@varicon.com.au"
              className={classes.emailLink}
              target="_blank"
              rel="noreferrer"
            >
              {' '}
              Contact Varicon Support
            </a>
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default SubscriptionExpire;
